<template>
  <Authenticated>
    <section v-for="site in siteMap" :key="site" class="main">
      <h2>{{ site.site }}</h2>
      <div class="unit-container-shell">
        <div v-if="showLeftScrollChevron(site.site)" class="scroll-chevron left" aria-hidden="true">&#8249;</div>
        <div v-if="showRightScrollChevron(site.site)" class="scroll-chevron right" aria-hidden="true">&#8250;</div>
        <div
          ref="unitContainers"
          class="unit-container"
          :data-site="site.site"
          @mouseenter="setHoveredUnitContainer"
          @mouseleave="clearHoveredUnitContainer"
          @wheel="handleUnitContainerWheel"
          @scroll="handleUnitContainerScroll"
        >
          <div v-for="eftpos in site.stations.pos" :key="eftpos" class="unit-list">
            <div class="unit">
              <div class="header">
                <h3>{{ eftpos.name }}</h3>
                <div class="icon-container">
                  <div v-if="eftpos.kiosk === true" class="state active">
                    <font-awesome-icon icon="person-chalkboard" class="active icon" />
                    <p>Kiosk</p>
                  </div>
                  <div class="state">
                    <font-awesome-icon icon="wifi" :class="(eftpos.active ? 'active' : 'discon') + ' icon'"></font-awesome-icon>
                    <p v-if="eftpos.active">Active</p>
                    <p v-else>Inactive</p>
                  </div>
                </div>
              </div>
              <button class="btn action-colour" @click="openPOSModal(eftpos)">Edit</button>
              <button class="btn" v-if="eftpos.float" @click.stop.prevent="openFloatModal(eftpos)">Float Control</button>
              <button class="btn action-colour" @click.stop.prevent="showCompanionModal(eftpos)">Companion</button>
              <CodeLogIn :station="eftpos.name" :site="eftpos.site" />
            </div>
          </div>
          <div class="new" @click.stop.prevent="openPOSModal(site.site, true)">Add POS station</div>
        </div>
      </div>
    </section>
    <POSStationModal :open="showPOSModal" :station="POSStation" :newStation="newStation" @update="updateStations" @close="close" />
    <CompanionModal :open="openCompanionModal" :station="POSStation" />
    <FloatModal
      :open="showFloatModal"
      :floatAmount="currentFloat"
      :stationName="floatStationName"
      :removals="floatRemovals"
      :history="floatHistory"
      @close="showFloatModal = false"
    />
    <div v-if="loading" class="loading-overlay">
      <LoadingSpinner :spinnerMessage="'Updating stations...'" />
    </div>
  </Authenticated>
</template>

<script>
import Authenticated from "../../_layouts/Authenticated.vue";
import POSStationModal from "./POSStationModal.vue";
import FloatModal from "./FloatModal.vue";
import { api } from "@monolith/helpers";
import dashify from "dashify";
import { mapGetters } from "vuex";
import { CodeLogIn, LoadingSpinner, CompanionModal } from "@monolith/components";

export default {
  name: "POSPage",
  components: {
    Authenticated,
    POSStationModal,
    FloatModal,
    CodeLogIn,
    CompanionModal,
    LoadingSpinner,
  },
  data() {
    return {
      showPOSModal: false,
      POSStation: {},
      newStation: false,
      openCompanionModal: false,
      hoveredUnitContainer: null,
      unitContainerObserver: null,
      unitContainerResizeObserver: null,
      visibleUnitContainers: new Set(),
      overflowingSiteMap: {},
      unitContainerScrollState: {},
      showFloatModal: false,
      currentFloat: 0,
      floatStationName: "",
      floatRemovals: [],
      floatHistory: [],
    };
  },
  methods: {
    close() {
      this.showPOSModal = false;
      this.newStation = false;
    },

    async openFloatModal(eftpos) {
      this.floatStationName = eftpos.name;
      this.currentFloat = 0;
      this.floatRemovals = [];
      this.floatHistory = [];
      const slug = dashify(eftpos.name);
      try {
        const [float, outgoings, history] = await Promise.all([
          api.get_station_float(eftpos.site, slug, true),
          api.get_float_outgoings(eftpos.site, slug),
          api.get_station_float(eftpos.site, slug, false, 2000),
        ]);
        this.currentFloat = float.rows[0].value || 0;
        this.floatRemovals = outgoings.rows
          .map((row) => row.value)
          .filter((doc) => doc && doc.station === slug)
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        this.floatHistory = history.rows
          .map((row) => row.doc)
          .filter((doc) => doc && doc.station === slug)
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      } catch {
        this.currentFloat = 0;
        this.floatRemovals = [];
        this.floatHistory = [];
      }
      this.showFloatModal = true;
    },

    setStationType(type) {
      if (type == "Kiosk") {
        this.openStation.kiosk = true;
        this.openStation.satellite = false;
      } else if (type == "Satellite") {
        this.showSatButton = true;
        this.openStation.satellite = true;
        this.openStation.kiosk = false;
      } else {
        this.openStation.kiosk = false;
        this.openStation.satellite = false;
      }
    },

    showCompanionModal(station) {
      this.POSStation = station;
      this.openCompanionModal = !this.openCompanionModal;
    },

    openPOSModal(incoming, newStation = false) {
      if (newStation) {
        this.POSStation = {
          name: null,
          site: incoming,
          kiosk: false,
          satellite: false,
          eftpos: {
            ip: null,
            port: null,
          },
        };
        this.newStation = newStation;
      } else {
        this.POSStation = incoming;
      }
      this.showPOSModal = !this.showPOSModal;
    },

    async updateStations(typeOfUpdate, typeOfStation, incomingStation) {
      this.$store.dispatch("loading", true);
      let newStations = [];
      this.siteMap.forEach((site) => {
        if (site.site == incomingStation.site) {
          site.stations[typeOfStation].forEach((station) => {
            if (typeOfUpdate === "update") {
              if (station.name == incomingStation.name) {
                newStations.push(this.removeActive(incomingStation));
              } else {
                newStations.push(station);
              }
            } else if (typeOfUpdate === "delete") {
              if (station.name != incomingStation.name) {
                newStations.push(this.removeActive(station));
              }
            }
          });
        } else {
          site.stations[typeOfStation].forEach((station) => {
            newStations.push(station);
          });
        }
      });

      await api.post("/api/company/update_stations", {
        doc: newStations,
        station_type: `get_${typeOfStation}`,
        site: incomingStation.site,
      });
      await this.$store.dispatch("initialSiteMap");
      this.$store.dispatch("loading", false);
      this.close();
    },

    removeActive(station) {
      if ("active" in station) {
        delete station.active;
      }
      return station;
    },

    getUnitContainerElements() {
      if (!this.$refs.unitContainers) {
        return [];
      }

      return Array.isArray(this.$refs.unitContainers) ? this.$refs.unitContainers : [this.$refs.unitContainers];
    },

    hasUnitContainerOverflow(siteName) {
      return Boolean(this.overflowingSiteMap[siteName]);
    },

    showLeftScrollChevron(siteName) {
      if (!this.hasUnitContainerOverflow(siteName)) {
        return false;
      }

      return Boolean(this.unitContainerScrollState[siteName].left);
    },

    showRightScrollChevron(siteName) {
      if (!this.hasUnitContainerOverflow(siteName)) {
        return false;
      }

      if (!this.unitContainerScrollState[siteName]) {
        return true;
      }

      return Boolean(this.unitContainerScrollState[siteName].right);
    },

    updateUnitContainerScrollState(element) {
      const siteName = element.dataset.site;
      if (!siteName) {
        return;
      }

      const maxScrollLeft = element.scrollWidth - element.clientWidth;
      const canScroll = maxScrollLeft > 1;
      if (!canScroll) {
        this.unitContainerScrollState = {
          ...this.unitContainerScrollState,
          [siteName]: {
            left: false,
            right: false,
          },
        };
        return;
      }

      const threshold = 4;
      this.unitContainerScrollState = {
        ...this.unitContainerScrollState,
        [siteName]: {
          left: element.scrollLeft > threshold,
          right: element.scrollLeft < maxScrollLeft - threshold,
        },
      };
    },

    updateUnitContainerOverflowState() {
      const nextOverflowingSiteMap = {};
      this.getUnitContainerElements().forEach((element) => {
        const siteName = element.dataset.site;
        if (!siteName) {
          return;
        }

        nextOverflowingSiteMap[siteName] = element.scrollWidth > element.clientWidth + 1;
        this.updateUnitContainerScrollState(element);
      });

      this.overflowingSiteMap = nextOverflowingSiteMap;
    },

    handleUnitContainerScroll(event) {
      this.updateUnitContainerScrollState(event.currentTarget);
    },

    initUnitContainerObserver() {
      if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
        this.updateUnitContainerOverflowState();
      } else {
        if (this.unitContainerObserver) {
          this.unitContainerObserver.disconnect();
        }

        this.visibleUnitContainers.clear();
        this.unitContainerObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.visibleUnitContainers.add(entry.target);
                return;
              }

              this.visibleUnitContainers.delete(entry.target);
              if (this.hoveredUnitContainer === entry.target) {
                this.hoveredUnitContainer = null;
              }
            });
          },
          { threshold: 0.2 }
        );

        this.getUnitContainerElements().forEach((element) => {
          this.unitContainerObserver.observe(element);
        });
      }

      if (typeof window !== "undefined" && "ResizeObserver" in window) {
        if (this.unitContainerResizeObserver) {
          this.unitContainerResizeObserver.disconnect();
        }

        this.unitContainerResizeObserver = new ResizeObserver(() => {
          this.updateUnitContainerOverflowState();
        });

        this.getUnitContainerElements().forEach((element) => {
          this.unitContainerResizeObserver.observe(element);
        });
      }

      this.$nextTick(() => {
        this.updateUnitContainerOverflowState();
      });
    },

    setHoveredUnitContainer(event) {
      this.hoveredUnitContainer = event.currentTarget;
    },

    clearHoveredUnitContainer(event) {
      if (this.hoveredUnitContainer === event.currentTarget) {
        this.hoveredUnitContainer = null;
      }
    },

    handleUnitContainerWheel(event) {
      const container = event.currentTarget;
      if (this.hoveredUnitContainer !== container) {
        return;
      }

      if (this.unitContainerObserver && !this.visibleUnitContainers.has(container)) {
        return;
      }

      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      if (maxScrollLeft <= 0) {
        return;
      }

      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (delta === 0) {
        return;
      }

      const atStart = container.scrollLeft <= 0;
      const atEnd = container.scrollLeft >= maxScrollLeft - 1;
      if ((delta < 0 && atStart) || (delta > 0 && atEnd)) {
        return;
      }

      event.preventDefault();
      container.scrollLeft += delta;
    },
  },
  computed: {
    ...mapGetters({
      siteMap: "siteMap",
      siteSettings: "siteSettings",
      companySettings: "companySettings",
      timezone: "timezone",
      sites: "sites",
      stripeCustomerDetails: "stripeCustomerDetails",
      getSubscribedProducts: "getSubscribedProducts",
      companyName: "companyName",
      loading: "loading",
      beamyProducts: "beamyProducts",
    }),
  },
  mounted() {
    this.$nextTick(() => {
      this.initUnitContainerObserver();
    });
  },

  beforeDestroy() {
    if (this.unitContainerObserver) {
      this.unitContainerObserver.disconnect();
      this.unitContainerObserver = null;
    }

    if (this.unitContainerResizeObserver) {
      this.unitContainerResizeObserver.disconnect();
      this.unitContainerResizeObserver = null;
    }

    this.visibleUnitContainers.clear();
    this.hoveredUnitContainer = null;
    this.overflowingSiteMap = {};
    this.unitContainerScrollState = {};
  },

  watch: {
    async siteMap(newVal, oldVal) {
      const calculateTotalStations = (siteMapArray) => {
        return siteMapArray.reduce((total, site) => {
          return total + site.stations.pos.length + site.stations.kds.length;
        }, 0);
      };

      const newCount = calculateTotalStations(newVal);
      const oldCount = calculateTotalStations(oldVal || []);

      await this.$store.dispatch("setSubscribedProducts");
      if (newCount !== oldCount) {
        await this.$store.dispatch("updateSubscription");
      }

      this.$nextTick(() => {
        this.initUnitContainerObserver();
      });
    },
  },
};
</script>

<style scoped lang="scss">
@use "@monolith/styles";

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--opacity-background);
  z-index: 1000;
}
.header {
  margin: 1.5rem;
}
.main {
  padding: 1rem;
  box-shadow: var(--box-shadow);
  background-color: var(--primary-colour);
  margin: 2rem;
  border-radius: 10px;
  max-width: 90%;
  min-height: 20rem;
  h2 {
    text-transform: capitalize;
  }
  .unit-container-shell {
    position: relative;
  }
  .scroll-chevron {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: var(--action-colour);
    font-size: 2rem;
    font-weight: 600;
    pointer-events: none;
    user-select: none;
    z-index: 2;
    opacity: 0.8;
    text-shadow: 0 0 10px color-mix(in srgb, var(--secondary-colour) 65%, transparent);
    animation: soft-chevron-pulse 2.4s ease-in-out infinite;

    &.left {
      left: 0.25rem;
    }

    &.right {
      right: 0.25rem;
    }
  }
  .unit-container {
    display: flex;
    flex-direction: row;
    // flex-wrap: wrap;
    align-items: flex-start;
    border-radius: 20px;
    margin: 1rem;
    height: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
    .new {
      background-color: var(--action-colour);
      color: var(--secondary-text-colour);
      border-radius: 0.5rem;
      padding: 1rem;
      margin: 0.5rem;
      box-shadow: var(--box-shadow);
      cursor: pointer;
      height: 25%;
      min-width: 16rem;
      text-align: center;
    }
    .unit {
      max-width: 95%;
      margin: 0.5rem;
      padding: 1rem;
      box-shadow: var(--box-shadow);
      border-radius: 10px;
      background-color: var(--tertiary-colour);
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 0 0 1rem 0;
        padding: 0.5rem;
        h3 {
          padding: 0;
          text-transform: capitalize;
          margin: 0;
        }
        .icon-container {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-end;
          gap: 1rem;

          .state {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.5rem;
            p {
              margin: 0;
            }
            .icon {
              font-size: 1.5rem;
              color: var(--text-colour);
            }
            .active {
              color: var(--action-colour);
            }
            .discon {
              color: red;
            }
          }
        }
      }
      .btn {
        display: flex;
        justify-content: center;
        margin: 0.5rem;
        min-width: 15rem;
        height: 2.5rem;
        align-items: center;
        background-color: var(--action-colour);
        color: var(--secondary-text-colour);
        padding: 0.5rem;
        cursor: pointer;
      }
      p {
        color: var(--action-colour);
      }
      &:active {
        box-shadow: none;
        translate: transform3d(0, 0, 0);
      }
    }
  }
}

@keyframes soft-chevron-pulse {
  0% {
    opacity: 0.45;
  }
  50% {
    opacity: 0.85;
  }
  100% {
    opacity: 0.45;
  }
}

@media only screen and (orientation: portrait) {
  .main {
    padding: 0.5rem 0;
    margin: 1rem !important;
    border-radius: 0.5rem;
    max-width: 95%;

    h2 {
      margin-left: 1rem;
    }

    .unit-container {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      justify-content: flex-start;
      overflow-x: auto;
      overflow-y: hidden;

      .new {
        min-width: 16rem;
        flex: 0 0 auto;
      }

      .unit {
        width: 16rem;
        min-width: 16rem;
        flex: 0 0 auto;
      }
    }
  }
}
</style>
